/**
 * @file 見開きで読むときの、左右に並べるページの組と送り先を決める (純関数) と、その設定を端末に覚える。
 *
 * @what: 紙に刷られたページ番号で組む (2-3、4-5…)。「1ページずらす」を入れた本は奇数から組む (1-2、3-4…)。
 *   組の先のページ (読む順で先) を「いまのページ」とし、閲覧位置・メモ・目次はそのページを指す。
 *   綴じ方向 (StudySource.binding) が右開きの本は、後のページを左に置く。
 * @why: 刷られた番号で組むのは、自炊で表紙や扉をどこまで含めたかによらず、紙の本と同じ見開きに
 *   なるようにするためである。スキャンの欠けで組がずれる本だけ、本ごとにずらせるようにする。
 *   見開きの入り切りは端末ごと、ずらすかは本ごとかつ端末ごとに覚える (代表の判断、2026-10-13)。
 * @failure: 例外を投げない。組の相手のページが取り込まれていなければ、1ページだけで出す。
 *   localStorage が使えない端末では、その画面のあいだだけ覚える。
 */

import type { StudyPage } from "../api/pages";
import type { StudySource } from "../api/sources";
import { printedPageOf } from "./sourcePages";

/** 見開きの1組である。first は読む順で先のページ、second は後のページ (無ければ null)。 */
export interface Spread {
  first: number;
  second: number | null;
}

/** 偶奇を0か1で返す (負の数でも同じ形で返す)。 */
function parity(n: number): number {
  return ((n % 2) + 2) % 2;
}

/**
 * そのページを含む見開きを返す。
 *
 * @param page リーダーの連番
 * @param shift 「1ページずらす」を入れているか (入れると奇数の刷られた番号から組む)
 */
export function spreadOf(
  pages: readonly StudyPage[],
  source: Pick<StudySource, "reader_offset">,
  page: number,
  shift: boolean,
): Spread {
  const has = (n: number) => pages.some((p) => p.page === n);
  const startsPair = parity(printedPageOf(source, page)) === (shift ? 1 : 0);
  if (startsPair) return { first: page, second: has(page + 1) ? page + 1 : null };
  if (has(page - 1)) return { first: page - 1, second: page };
  return { first: page, second: null };
}

/** 次の見開きへ送るときに開くページ (無ければ null)。 */
export function nextSpreadPage(pages: readonly StudyPage[], spread: Spread): number | null {
  const last = spread.second ?? spread.first;
  return pages.find((p) => p.page > last)?.page ?? null;
}

/** 前の見開きへ送るときに開くページ (無ければ null)。開いた先で spreadOf がその組の先のページへ直す。 */
export function prevSpreadPage(pages: readonly StudyPage[], spread: Spread): number | null {
  let found: number | null = null;
  for (const p of pages) {
    if (p.page >= spread.first) break;
    found = p.page;
  }
  return found;
}

/** 後のページを左に置くか (右開きの本)。 */
export function secondOnLeft(source: Pick<StudySource, "binding">): boolean {
  return source.binding === "right";
}

const SPREAD_KEY = "bb.study.reader.spread";
const shiftKey = (sourceId: string) => `bb.study.reader.spreadShift.${sourceId}`;

/** 覚えている「見開きで読む」の値 (覚えていなければ切)。 */
export function readSpreadEnabled(): boolean {
  try {
    return window.localStorage.getItem(SPREAD_KEY) === "1";
  } catch {
    return false;
  }
}

/** 「見開きで読む」の値を覚える。 */
export function writeSpreadEnabled(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(SPREAD_KEY, "1");
    else window.localStorage.removeItem(SPREAD_KEY);
  } catch {
    // 覚えられなくても、その画面のあいだは切り替えが効く
  }
}

/** その本で「1ページずらす」を入れているか。 */
export function readSpreadShift(sourceId: string): boolean {
  try {
    return window.localStorage.getItem(shiftKey(sourceId)) === "1";
  } catch {
    return false;
  }
}

/** その本の「1ページずらす」を覚える。 */
export function writeSpreadShift(sourceId: string, on: boolean): void {
  try {
    if (on) window.localStorage.setItem(shiftKey(sourceId), "1");
    else window.localStorage.removeItem(shiftKey(sourceId));
  } catch {
    // 覚えられなくても、その画面のあいだは切り替えが効く
  }
}
